import { Injectable, InjectionToken, Provider } from '@angular/core';
import { mockLekPage, mockLekSponsorPage } from '../../mock-data/lek.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiCreateLek, ApiLek, ApiLekDateRangePossibilities, ApiLekSponsor, ApiUpdateLek } from '../api-types/lek.api';
import { AppCreateLek, AppUpdateLek, Lek, LekDateRangePossibilities, LekSponsor } from '../app-types/lek.app';
import {
	transformApiLekDatePossibilitiesToAppLekDateRangePossibilities,
	transformApiLekSponsorsToAppLekSponsors,
	transformApiLeksToAppLeks,
	transformApiLekToAppLek,
	transformAppCreateLekToApiCreateLek,
	transformAppUpdateLekToApiUpdateLek,
} from '../transformers/lek.transform';
import { BaseRequestService } from './base-request.service';
import { ApiResponse, Metadata } from '../api-types/api-response.api';

export const LEK_PROVIDER = new InjectionToken<LekProvider>('LEK_PROVIDER');

export interface LekProvider {
	create(data: AppCreateLek): Promise<void>;
	update(assistanceId: number | string, data: AppUpdateLek): Promise<Lek>;
	getValidDateRanges(clientId: string | number): Promise<LekDateRangePossibilities>;
	getAllByAssignment(
		assignmentId: string | number,
		range?: number,
		offset?: number,
		searchTerm?: string,
	): Promise<PaginatedResponse<Lek>>;
	getLekSponsors(range?: number, offset?: number, searchTerm?: string): Promise<PaginatedResponse<LekSponsor>>;
	single(lekId: string | number): Promise<ApiResponse<Lek, Metadata>>;
	deleteItem(lekId: string | number): Promise<void>;
}

@Injectable()
export class LekApi extends BaseRequestService implements LekProvider {
	public async getLekSponsors(
		range?: number,
		offset?: number,
		searchTerm?: string,
	): Promise<PaginatedResponse<LekSponsor>> {
		const response = await this.get<PaginatedResponse<ApiLekSponsor>>('categories/sponsors', {
			range,
			offset,
			searchTerm,
		});
		return {
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
			entities: transformApiLekSponsorsToAppLekSponsors(response.entities),
		};
	}
	public async create(data: AppCreateLek) {
		const body = transformAppCreateLekToApiCreateLek(data);
		await this.post<void, ApiCreateLek>(`assistanceResult`, body);

		return;
	}

	public async update(assistanceId: number | string, data: AppUpdateLek): Promise<Lek> {
		const body = transformAppUpdateLekToApiUpdateLek(data);
		const response = await this.put<ApiLek, ApiUpdateLek>(`assistanceResult/${assistanceId}`, body);
		return transformApiLekToAppLek(response);
	}

	/**
	 * @deprecated Use `getValidDateRangeForLEKOfAssignment` for LEK date range validation instead.
	 */
	public async getValidDateRanges(clientId: string | number) {
		const response = await this.get<ApiLekDateRangePossibilities>(`validation/AssistanceResult/clients/${clientId}`);
		return transformApiLekDatePossibilitiesToAppLekDateRangePossibilities(response);
	}

	public async getAllByAssignment(assignmentId: string | number, range?: number, offset?: number, searchTerm?: string) {
		const result = await this.get<PaginatedResponse<ApiLek>>(`assistanceResult/assignment/${assignmentId}`, {
			range,
			offset,
			searchTerm,
		});
		const transformedResult: PaginatedResponse<Lek> = {
			...result,
			entities: transformApiLeksToAppLeks(result.entities),
		};
		return transformedResult;
	}

	public async single(lekId: string | number): Promise<ApiResponse<Lek, Metadata>> {
		const response = await this.get<ApiResponse<ApiLek, Metadata>>(`assistanceResult/${lekId}`);
		const lek = transformApiLekToAppLek(response.data);
		return { data: lek, metadata: response.metadata };
	}

	public async deleteItem(lekId: string | number): Promise<void> {
		await this.remove<void>(`assistanceResult/${lekId}`);
		return;
	}
}

@Injectable()
export class LekMockApi implements LekProvider {
	public getLekSponsors(): Promise<PaginatedResponse<LekSponsor>> {
		return Promise.resolve(mockLekSponsorPage);
	}
	public async create(_data: AppCreateLek): Promise<void> {
		console.warn('⛔️⛔️⛔️ LEK Mock Service->create: Mock Data:', _data);
		return;
	}
	public async getValidDateRanges(_clientId: string | number): Promise<LekDateRangePossibilities> {
		console.warn('⛔️⛔️⛔️ LEK Mock Service->getValidDateRanges: Mock Data:', _clientId);
		return {
			validEndDateRange: {
				min: new Date('2024-01-01'),
				max: new Date('2024-12-31'),
			},
			validStartDateRange: {
				min: new Date('2024-01-01'),
				max: new Date('2024-12-31'),
			},
		};
	}
	public async getAllByAssignment(
		_assignmentId: string | number,
		_range?: number,
		_offset?: number,
		_searchTerm?: string,
	): Promise<PaginatedResponse<Lek>> {
		console.warn(
			'⛔️⛔️⛔️ LEK Mock Service->getAllByAssignment: Mock Data:',
			_assignmentId,
			_range,
			_offset,
			_searchTerm,
		);
		return {
			entities: [],
			range: 0,
			offset: 0,
			totalEntities: 0,
		};
	}

	public async single(_lekId: string | number): Promise<ApiResponse<Lek, Metadata>> {
		console.warn('⛔️⛔️⛔️ LEK Mock Service->single: Mock Data:', _lekId);
		const lek = mockLekPage.entities[0];
		return { data: lek, metadata: { editOnlyFields: [] } };
	}

	public async update(_assistanceId: number | string, _data: AppUpdateLek): Promise<Lek> {
		console.warn('⛔️⛔️⛔️ LEK Mock Service->update: Mock Data:', _data);
		return mockLekPage.entities[0];
	}

	public async deleteItem(_lekId: string | number): Promise<void> {
		console.warn('⛔️⛔️⛔️ LEK Mock Service->deleteItem: Mock Data:', _lekId);
		return;
	}
}

export const provideLekApi: Provider = {
	provide: LEK_PROVIDER,
	useClass: LekApi,
};
